// src/components/layout/Header.tsx

import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { CircleUser, User, KeyRound, Monitor, ShieldCheck, DoorOpen, Settings, FilePlus2, CalendarClock } from 'lucide-react';
import { RootState } from 'store';
import { logout as logoutAction } from 'store/slices/authSlice';
import { showToast } from 'store/slices/toastSlice';
import { authService } from 'services/authService';
import { usePermission } from 'hooks/usePermission';
import ChangePasswordModal from 'components/auth/ChangePasswordModal';
import SessionsModal from 'components/auth/SessionsModal';
import MfaSettingsModal from 'components/auth/MfaSettingsModal';
import ProfileModal from 'components/auth/ProfileModal';
import GlobalSearch from 'components/layout/GlobalSearch';

export default function Header() {
  const dispatch  = useDispatch();
  const navigate  = useNavigate();
  const [showMenu, setShowMenu]     = useState(false);
  const [showChangePw, setShowChangePw] = useState(false);
  const [showSessions, setShowSessions] = useState(false);
  const [showMfa, setShowMfa] = useState(false);
  const [showProfile, setShowProfile] = useState(false);

  const { user } = useSelector((s: RootState) => s.auth);
  const canManageMeetings = usePermission('canManageMeetings');

  function notImplementedYet() {
    dispatch(showToast('🛠️ Design only for now — functionality coming soon'));
  }

  async function handleLogoutAll() {
    setShowMenu(false);
    try { await authService.logoutAll(); } catch { /* ignore */ }
    dispatch(logoutAction());
    navigate('/login');
  }

  return (
    <>
      <header className="relative flex items-center gap-3 px-4 py-2.5 bg-[var(--om-panel-97)] border-b border-[var(--om-border)] shadow-sm flex-shrink-0 z-50 backdrop-blur-sm">
        {/* Brand */}
        <button
          onClick={() => navigate('/dashboard')}
          style={{ cursor: 'pointer' }}
          className="text-lg font-black bg-gradient-to-r from-blue-500 to-blue-300 bg-clip-text text-transparent tracking-widest"
        >
          TODO
        </button>

        {/* Quick actions — truly centered (absolute) so it stays put
            regardless of how wide either side's content grows. */}
        <div className="hidden lg:flex items-center absolute left-1/2 -translate-x-1/2 rounded-full border border-[var(--om-border)] bg-[var(--om-card)] p-1 gap-1">
          <button
            onClick={() => navigate('/tasks/assign')}
            className="flex items-center gap-1.5 pl-3 pr-4 py-1.5 rounded-full text-[12px] font-bold text-[var(--om-text-muted)] hover:text-blue-400 hover:bg-blue-500/10 transition-all"
          >
            <FilePlus2 className="w-4 h-4" strokeWidth={2} /> Assign Task
          </button>
          {canManageMeetings && (
            <button
              onClick={() => navigate('/calendar')}
              className="flex items-center gap-1.5 pl-3 pr-4 py-1.5 rounded-full text-[12px] font-bold text-[var(--om-text-muted)] hover:text-purple-400 hover:bg-purple-500/10 transition-all"
            >
              <CalendarClock className="w-4 h-4" strokeWidth={2} /> Assign Meeting
            </button>
          )}
        </div>

        <div className="flex-1" />

        {/* Search — moved in with the rest of the right-side icon cluster */}
        <GlobalSearch />

        {/* Settings — design placeholder, no behavior wired up yet */}
        <button
          onClick={notImplementedYet}
          title="Settings"
          className="w-9 h-9 flex items-center justify-center rounded-full border border-[var(--om-border)] bg-[var(--om-card)] text-[var(--om-text-faint)] hover:text-blue-400 hover:border-blue-500/30 transition-all"
        >
          <Settings className="w-4 h-4" strokeWidth={2} />
        </button>

        {/* User menu — opens on hover; icon on the left, name/role on the right */}
        <div
          className="relative"
          onMouseEnter={() => setShowMenu(true)}
          onMouseLeave={() => setShowMenu(false)}
        >
          <button
            onClick={() => setShowMenu(v => !v)}
            style={{ cursor: 'pointer' }}
            className="flex items-center gap-2.5 pl-1.5 pr-3 py-1.5 rounded-full border border-[var(--om-border)] bg-[var(--om-card)] hover:border-blue-500/30 transition-all"
            title="User menu"
          >
            <CircleUser className="w-8 h-8 text-[var(--om-text-secondary)] flex-shrink-0" strokeWidth={1.75} />
            {user && (
              <span className="hidden sm:flex flex-col items-start leading-tight">
                <span className="text-sm font-black text-[var(--om-text)]">{user.name}</span>
                <span className="text-[11px] text-[var(--om-text-secondary)] font-semibold">{user.department || user.role || ''}</span>
              </span>
            )}
          </button>

          {showMenu && (
              <div className="absolute right-0 top-10 z-50 w-52 bg-[var(--om-panel)] border border-[var(--om-border)] rounded-xl shadow-2xl overflow-hidden">
                {/* User info */}
                <div className="px-4 py-3 border-b border-[var(--om-border)]">
                  <p className="text-sm font-bold text-[var(--om-text)]">{user?.email}</p>
                </div>

                {/* Menu items */}
                <button
                  onClick={() => { setShowMenu(false); setShowProfile(true); }}
                  className="w-full text-left px-4 py-2.5 text-xs text-[var(--om-text-muted)] hover:text-[var(--om-text)] hover:bg-[var(--om-card)] transition-colors flex items-center gap-2"
                >
                  <User className="w-4 h-4" strokeWidth={2} /> Edit Profile
                </button>

                <button
                  onClick={() => { setShowMenu(false); setShowChangePw(true); }}
                  className="w-full text-left px-4 py-2.5 text-xs text-[var(--om-text-muted)] hover:text-[var(--om-text)] hover:bg-[var(--om-card)] transition-colors flex items-center gap-2"
                >
                  <KeyRound className="w-4 h-4" strokeWidth={2} /> Change Password
                </button>

                <button
                  onClick={() => { setShowMenu(false); setShowSessions(true); }}
                  className="w-full text-left px-4 py-2.5 text-xs text-[var(--om-text-muted)] hover:text-[var(--om-text)] hover:bg-[var(--om-card)] transition-colors flex items-center gap-2"
                >
                  <Monitor className="w-4 h-4" strokeWidth={2} /> Active Sessions
                </button>

                <button
                  onClick={() => { setShowMenu(false); setShowMfa(true); }}
                  className="w-full text-left px-4 py-2.5 text-xs text-[var(--om-text-muted)] hover:text-[var(--om-text)] hover:bg-[var(--om-card)] transition-colors flex items-center gap-2"
                >
                  <ShieldCheck className="w-4 h-4" strokeWidth={2} /> Two-Factor Authentication
                </button>

                <button
                  onClick={handleLogoutAll}
                  className="w-full text-left px-4 py-2.5 text-xs text-red-400 hover:text-red-300 hover:bg-red-500/5 transition-colors flex items-center gap-2 border-t border-[var(--om-border)]"
                >
                  <DoorOpen className="w-4 h-4" strokeWidth={2} /> Logout
                </button>
              </div>
          )}
        </div>
      </header>

      {/* Change password modal */}
      {showChangePw && (
        <ChangePasswordModal onClose={() => setShowChangePw(false)} />
      )}

      {/* Active sessions modal */}
      {showSessions && (
        <SessionsModal onClose={() => setShowSessions(false)} />
      )}

      {/* MFA settings modal */}
      {showMfa && (
        <MfaSettingsModal onClose={() => setShowMfa(false)} />
      )}

      {/* Edit profile modal */}
      {showProfile && (
        <ProfileModal onClose={() => setShowProfile(false)} />
      )}
    </>
  );
}
